import { useEffect, useMemo, useState } from 'react';
import {
  Box, Card, Stack, Typography, Grid, Button, TextField, MenuItem, IconButton,
  Tabs, Tab, Chip, Tooltip, Divider, Alert, InputAdornment,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  CircularProgress, LinearProgress,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import {
  Key as KeyIcon, Search as SearchIcon, AutoFixHigh as BulkIcon,
  AddCircleOutline as GenerateIcon, RestartAlt as ResetIcon,
  Block as BlockIcon, LockOpen as UnblockIcon, DeleteForever as DeleteIcon,
  ContentCopy as CopyIcon, Refresh as RefreshIcon,
  Groups as StudentsIcon, VerifiedUser as ActivePinIcon,
  KeyOff as NoPinIcon, Sms as SmsIcon, Tune as SettingsIcon,
  Send as SendIcon, Link as LinkIcon,
  Smartphone as MomoIcon,
} from '@mui/icons-material';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import { alpha } from '@mui/material/styles';
import ModuleHeader from '../../components/ModuleHeader';
import API from '../../BaseURL';
import swal from '../../utils/swal';

const FILTERS = [
  { v: 'all',     label: 'All students' },
  { v: 'without', label: 'Needs a code' },
  { v: 'with',    label: 'Code live' },
  { v: 'used',    label: 'Parent set up' },
  { v: 'blocked', label: 'Blocked' },
];

const fmtWhen = (v) => {
  if (!v) return '';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '';
  const days = Math.round((Date.now() - d.getTime()) / 86400000);
  if (days === 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days > 1)   return `${days} days ago`;
  return `in ${Math.abs(days)} day${Math.abs(days) === 1 ? '' : 's'}`;
};

export default function StudentPortalSetupPage() {
  const [counts, setCounts] = useState({
    students: 0, live: 0, onboarded: 0, blocked: 0, without: 0,
  });
  const [classes, setClasses] = useState([]);

  const [q, setQ]           = useState('');
  const [filter, setFilter] = useState('all');
  const [cla, setCla]       = useState('');

  const [rows, setRows]     = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyInd, setBusyInd] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);

  // Portal link + SMS wording, edited in a dialog off the toolbar.
  const [cfgOpen, setCfgOpen] = useState(false);
  const [cfg, setCfg] = useState({ link: '', template: '', school: '', otp_ttl_days: 7 });
  const [cfgSaving, setCfgSaving] = useState(false);
  const [momoOpen, setMomoOpen] = useState(false);
  const [momo, setMomo] = useState({});
  const [momoSaving, setMomoSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [s, c] = await Promise.all([
        API.get('/parent-portal/students', { params: { q, filter, cla } }),
        API.get('/parent-portal/counters'),
      ]);
      setRows(s.data || []);
      setCounts(c.data || {});
    } catch (e) { swal.error(swal.pickError(e, 'Load failed')); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    // Only classes the school is actually running. `Alumna` is a bucket
    // for learners who have finished, not a class anybody is taught in —
    // offering it here would filter the roster down to people the portal
    // is no longer for. Retired classes go for the same reason. Kept in
    // the school's own order (Creche → J.H.S-3), never alphabetical.
    API.get('/classes')
      .then(r => setClasses(
        (r.data || [])
          .filter(c => Number(c.is_active ?? 1) === 1)
          .map(c => c.class || c.cla || c.name)
          .filter(Boolean)
          .filter(name => String(name).trim().toLowerCase() !== 'alumna')
      ))
      .catch(() => {});
    load();
    /* eslint-disable-next-line */
  }, []);
  // Re-search when filter/class changes; debounce-on-Enter for the search box
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter, cla]);

  const act = async (ind, fn, label) => {
    setBusyInd(ind);
    try {
      const r = await fn(ind);
      if (r?.data?.otp) {
        // Shown once, here, so the office can read it to a parent who
        // walked in. It is also on the row until it is used.
        swal.success(`${label} — code ${r.data.otp}`);
      } else {
        swal.success(r?.data?.message || label);
      }
      await load();
    } catch (e) { swal.error(swal.pickError(e, `${label} failed`)); }
    finally { setBusyInd(null); }
  };

  const generate = (ind)  => act(ind, (i) => API.post(`/parent-portal/pin/${encodeURIComponent(i)}/generate`), 'Code issued');
  const reset    = async (ind) => {
    const ok = await swal.confirm({
      title: 'Issue a fresh code?',
      text: `${ind} gets a new 6-digit one-time code. Anything they hold now stops working — `
          + 'including a passcode the parent has already chosen, which they will have to set again.',
      tone: 'warning', confirmText: 'Yes, issue',
    });
    if (!ok) return;
    return act(ind, (i) => API.post(`/parent-portal/pin/${encodeURIComponent(i)}/reset`), 'Fresh code issued');
  };
  const block    = async (ind) => {
    const ok = await swal.confirm({
      title: 'Block this login?', text: 'The parent will not be able to sign in until unblocked.',
      tone: 'danger', confirmText: 'Yes, block',
    });
    if (!ok) return;
    return act(ind, (i) => API.post(`/parent-portal/pin/${encodeURIComponent(i)}/block`), 'Login blocked');
  };
  const unblock  = (ind) => act(ind, (i) => API.post(`/parent-portal/pin/${encodeURIComponent(i)}/unblock`), 'Login unblocked');
  const remove   = async (ind) => {
    const ok = await swal.confirm({
      title: 'Remove portal access?',
      text: 'The parent loses access entirely, including any passcode they set. '
          + 'You can issue a new code later.',
      tone: 'danger', confirmText: 'Yes, remove',
    });
    if (!ok) return;
    return act(ind, (i) => API.delete(`/parent-portal/pin/${encodeURIComponent(i)}`), 'Portal access removed');
  };

  // ── Issuing and sending at scale ───────────────────────────────
  //
  // Scope follows the Class filter. That is deliberate: the office has
  // already told the page which class it is looking at, and a separate
  // class picker inside every dialog would be a second place to get it
  // wrong. With no class chosen, scope is the whole school — and the
  // confirmation says so in those words rather than "bulk".
  const scopeLabel = cla || 'the whole school';

  const issue = async (mode) => {
    const replacing = mode === 'all';
    const ok = await swal.confirm({
      title: replacing
        ? `Replace every code in ${scopeLabel}?`
        : `Issue codes for ${scopeLabel}?`,
      text: replacing
        ? 'Every learner in scope gets a NEW one-time code. Any parent who has already '
          + 'set a passcode will lose it and have to start again from the SMS. '
          + 'Use this only after something has gone wrong.'
        : 'Learners with no code — or whose code expired or was already used — get a '
          + 'fresh one. Parents who are already set up are left alone.',
      tone: replacing ? 'danger' : 'warning',
      confirmText: replacing ? 'Yes, replace them all' : 'Yes, issue codes',
    });
    if (!ok) return;
    setBulkBusy(true);
    try {
      const r = await API.post('/parent-portal/otp/issue', { cla, mode });
      swal.success(r.data?.message || 'Codes issued');
      await load();
    } catch (e) { swal.error(swal.pickError(e, 'Could not issue codes')); }
    finally { setBulkBusy(false); }
  };

  const sendMany = async () => {
    const ok = await swal.confirm({
      title: `Text the codes for ${scopeLabel}?`,
      text: 'Each parent receives their own child\'s index number, their one-time code and '
          + 'the portal link. Only live codes are sent — expired or already-used ones are '
          + 'skipped and reported back. This spends SMS credits.',
      tone: 'warning', confirmText: 'Yes, send',
    });
    if (!ok) return;
    setBulkBusy(true);
    try {
      const r = await API.post('/parent-portal/otp/send', cla ? { cla } : { all: true });
      const d = r.data || {};
      const lines = [d.message];
      if (d.skipped?.length) {
        lines.push('', 'Skipped:');
        for (const s of d.skipped.slice(0, 12)) lines.push(`• ${s.name || s.ind} — ${s.reason}`);
        if (d.skipped.length > 12) lines.push(`…and ${d.skipped.length - 12} more`);
      }
      swal.info(lines.join('\n'), 'SMS run complete');
      await load();
    } catch (e) { swal.error(swal.pickError(e, 'Could not send')); }
    finally { setBulkBusy(false); }
  };

  const sendOne = (r) => act(r.ind, (i) =>
    API.post('/parent-portal/otp/send', { inds: [i] }), `Code texted to ${r.phone || 'guardian'}`);

  // ── Mobile money ───────────────────────────────────────────────
  //
  // The secrets are never sent back down, so the fields start blank and
  // an untouched blank means "keep what is stored". `*_set` tells the
  // office whether something is there without revealing what.
  const openMomo = async () => {
    setMomoOpen(true);
    try {
      const r = await API.get('/parent-portal/momo');
      setMomo({ ...r.data, api_key: '', api_secret: '', callback_secret: '' });
    } catch (e) { swal.error(swal.pickError(e, 'Could not load mobile money settings')); }
  };
  const saveMomo = async () => {
    setMomoSaving(true);
    try {
      await API.put('/parent-portal/momo', momo);
      swal.success('Mobile money settings saved');
      setMomoOpen(false);
    } catch (e) { swal.error(swal.pickError(e, 'Could not save')); }
    finally { setMomoSaving(false); }
  };
  const setM = (k) => (e) => setMomo(m => ({
    ...m, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value,
  }));

  // ── Portal link + message wording ──────────────────────────────
  const openCfg = async () => {
    setCfgOpen(true);
    try {
      const r = await API.get('/parent-portal/settings');
      setCfg(r.data || {});
    } catch (e) { swal.error(swal.pickError(e, 'Could not load portal settings')); }
  };
  const saveCfg = async () => {
    setCfgSaving(true);
    try {
      const r = await API.put('/parent-portal/settings',
        { link: cfg.link, template: cfg.template });
      setCfg(r.data || cfg);
      swal.success('Portal settings saved');
      setCfgOpen(false);
    } catch (e) { swal.error(swal.pickError(e, 'Could not save')); }
    finally { setCfgSaving(false); }
  };

  // Rendered here rather than fetched, so the office sees the effect of
  // every keystroke instead of having to save to find out.
  const cfgPreview = useMemo(() => {
    // The saved override wins while the office is typing one, otherwise
    // show what the server auto-detected — the same order the backend
    // applies, so the preview never promises a different link.
    const base = String(cfg.link || cfg.resolved_link || '').replace(/\/+$/, '');
    return String(cfg.template || '')
      .replace(/\{school\}/g,   cfg.school || 'the school')
      .replace(/\{student\}/g,  'AMA MENSAH')
      .replace(/\{index\}/g,    'JESS_563')
      .replace(/\{otp\}/g,      '481902')
      .replace(/\{days\}/g,     String(cfg.otp_ttl_days ?? 7))
      .replace(/\{link\}/g,     base || '(no link)')
      .replace(/\{signin\}/g,   base ? `${base}/login?ind=JESS_563` : '(no link)')
      .replace(/\{class\}/g,    'Class 1')
      .replace(/\{guardian\}/g, 'MR MENSAH')
      .replace(/\s+/g, ' ').trim();
  }, [cfg]);
  // 160 GSM-7 characters per SMS segment; over that the gateway bills
  // per 153. Worth showing, because a template edit that adds one word
  // can double the cost of a 230-parent run.
  const segments = Math.max(1, Math.ceil(cfgPreview.length / (cfgPreview.length <= 160 ? 160 : 153)));

  const copyCreds = async (r) => {
    const text = `Portal login\nUsername: ${r.ind}\nPIN: ${r.pin}`;
    try {
      await navigator.clipboard.writeText(text);
      swal.success(`Copied credentials for ${r.name || r.ind}`);
    } catch {
      swal.info(text, 'Credentials');
    }
  };

  // Each tile is also the filter it describes — the number a reader
  // reacts to ("47 without a PIN") is the list they then want to see,
  // and making them hunt for the matching tab is a step for nothing.
  const counterTiles = useMemo(() => [
    { label: 'On roll now', value: counts.students, tone: 'primary',
      icon: <StudentsIcon fontSize="small" />, filter: 'all',
      hint: 'Excludes learners who have completed' },
    { label: 'Code sent, waiting', value: counts.live, tone: 'info',
      icon: <SmsIcon fontSize="small" />, filter: 'with',
      hint: 'A one-time code is live — the parent has not signed in yet' },
    { label: 'Parent set up', value: counts.onboarded, tone: 'success',
      icon: <ActivePinIcon fontSize="small" />, filter: 'used',
      hint: 'The parent chose their own passcode. The school cannot see it.' },
    { label: 'Needs a code', value: counts.without, tone: 'warning',
      icon: <NoPinIcon fontSize="small" />, filter: 'without',
      hint: 'No code issued, or the last one expired or was used up' },
  ], [counts]);

  // What one row's credential is doing, in the office's words.
  const STATE_CHIP = {
    none:    { label: 'No code',    color: 'warning' },
    active:  { label: 'Code live',  color: 'info' },
    expired: { label: 'Expired',    color: 'error' },
    used:    { label: 'Parent set', color: 'success' },
    blocked: { label: 'Blocked',    color: 'error' },
  };

  return (
    <Box sx={{ maxWidth: 1500, mx: 'auto' }}>
      <ModuleHeader
        icon={<KeyIcon />}
        title="Student Portal Setup"
        subtitle="Generate the 6-character PIN parents use to sign into the same login page — their child's index number is the username"
      />

      {/* Counters — clickable, each one filters the table below it */}
      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        {counterTiles.map(c => {
          const selected = filter === c.filter;
          return (
            <Grid item xs={6} md={3} key={c.label}>
              <Tooltip title={c.hint}>
                <Card
                  variant="outlined"
                  onClick={() => setFilter(c.filter)}
                  sx={{
                    p: 1.5, cursor: 'pointer', position: 'relative', overflow: 'hidden',
                    borderColor: (t) => selected
                      ? t.palette[c.tone].main
                      : alpha(t.palette[c.tone].main, 0.28),
                    bgcolor: (t) => alpha(t.palette[c.tone].main, selected ? 0.10 : 0.04),
                    transition: 'background-color .15s, border-color .15s, transform .15s',
                    '&:hover': { transform: 'translateY(-2px)' },
                    '&::before': {
                      content: '""', position: 'absolute', left: 0, top: 0, bottom: 0,
                      width: 3, bgcolor: `${c.tone}.main`,
                      opacity: selected ? 1 : 0.5,
                    },
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1.2}>
                    <Box sx={{
                      width: 34, height: 34, borderRadius: 1.5, flexShrink: 0,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      bgcolor: (t) => alpha(t.palette[c.tone].main, 0.16),
                      color: `${c.tone}.main`,
                    }}>
                      {c.icon}
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{
                        fontWeight: 900, lineHeight: 1.1, fontSize: 24,
                        color: `${c.tone}.dark`, fontVariantNumeric: 'tabular-nums',
                      }}>
                        {Number(c.value || 0).toLocaleString()}
                      </Typography>
                      <Typography noWrap sx={{
                        fontSize: 10, fontWeight: 800, letterSpacing: 0.5,
                        textTransform: 'uppercase', color: 'text.secondary',
                      }}>
                        {c.label}
                      </Typography>
                    </Box>
                  </Stack>
                </Card>
              </Tooltip>
            </Grid>
          );
        })}
      </Grid>

      {/* Toolbar */}
      <Card sx={{ mb: 2, p: 2 }}>
        <Grid container spacing={1.2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField fullWidth size="small" value={q} placeholder="Search index, student, guardian, phone, PIN…"
              onChange={e => setQ(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && load()}
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
              }} />
          </Grid>
          <Grid item xs={12} sm={4} md={2}>
            <TextField select fullWidth size="small" label="Class" value={cla} onChange={e => setCla(e.target.value)}>
              <MenuItem value=""><em>All classes</em></MenuItem>
              {classes.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} md={3.5}>
            <Tabs value={filter} onChange={(_, v) => setFilter(v)} variant="scrollable"
              allowScrollButtonsMobile
              sx={{ minHeight: 36, '& .MuiTab-root': { minHeight: 36, fontSize: 12, fontWeight: 700, textTransform: 'none', minWidth: 'auto', px: 1.5 } }}>
              {FILTERS.map(f => <Tab key={f.v} value={f.v} label={f.label} />)}
            </Tabs>
          </Grid>
          <Grid item xs={3} md={0.5}>
            <Tooltip title="Refresh">
              <span>
                <Button fullWidth variant="outlined" size="small" onClick={load} disabled={loading}
                  sx={{ textTransform: 'none', fontWeight: 700, height: 38, minWidth: 0, px: 0 }}>
                  {loading ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}
                </Button>
              </span>
            </Tooltip>
          </Grid>
          <Grid item xs={9} md={2}>
            <Stack direction="row" spacing={0.8}>
              <Tooltip title={`Issue one-time codes for ${cla || 'the whole school'} — only where one is needed`}>
                <span style={{ flex: 1 }}>
                  <Button fullWidth variant="contained" size="small" startIcon={<BulkIcon />}
                    disabled={bulkBusy} onClick={() => issue('missing')}
                    sx={{ textTransform: 'none', fontWeight: 800, height: 38 }}>
                    Issue
                  </Button>
                </span>
              </Tooltip>
              <Tooltip title={`Text the live codes to guardians in ${cla || 'the whole school'}`}>
                <span style={{ flex: 1 }}>
                  <Button fullWidth variant="contained" color="success" size="small"
                    startIcon={<SmsIcon />} disabled={bulkBusy} onClick={sendMany}
                    sx={{ textTransform: 'none', fontWeight: 800, height: 38 }}>
                    Send
                  </Button>
                </span>
              </Tooltip>
              <Tooltip title="Portal link and message wording">
                <span>
                  <Button variant="outlined" size="small" onClick={openCfg}
                    sx={{ minWidth: 0, px: 1, height: 38 }}>
                    <SettingsIcon fontSize="small" />
                  </Button>
                </span>
              </Tooltip>
              <Tooltip title="Mobile money payments">
                <span>
                  <Button variant="outlined" size="small" onClick={openMomo}
                    sx={{ minWidth: 0, px: 1, height: 38 }}>
                    <MomoIcon fontSize="small" />
                  </Button>
                </span>
              </Tooltip>
            </Stack>
          </Grid>
        </Grid>
      </Card>

      {bulkBusy && <LinearProgress sx={{ mb: 1 }} />}

      {/* The link is what makes the SMS actionable. Without it the
          parent gets a code and no idea where to type it, so this is a
          warning rather than a quiet blank in a settings dialog. */}
      {cfg.link_source === 'none' && counts.students > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}
          action={<Button size="small" onClick={openCfg} sx={{ fontWeight: 700 }}>Set link</Button>}>
          The portal link could not be worked out from this connection, so the SMS would
          name a code with no address to use it at. Set one before sending.
        </Alert>
      )}

      {/* Portal link + message wording */}
      <Dialog open={cfgOpen} onClose={() => setCfgOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>Portal link &amp; message</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2.2} sx={{ pt: 0.5 }}>
            {/* The link normally needs no input at all: the server reads
                the origin this page is being served from, which across
                several schools on one codebase is always the right
                answer and never has to be maintained. */}
            <Alert severity={cfg.link_source === 'request' ? 'success' : 'info'}
              icon={<LinkIcon fontSize="small" />} sx={{ borderRadius: 2 }}>
              {cfg.link_source === 'request' && (
                <>
                  Detected automatically from this address:{' '}
                  <b style={{ wordBreak: 'break-all' }}>{cfg.resolved_link}</b>
                  <Typography component="div" sx={{ fontSize: 11.5, mt: 0.4, opacity: 0.85 }}>
                    Each school gets its own, with nothing to set up. Only fill the box below
                    if parents reach the portal on a different address from the one you use.
                  </Typography>
                </>
              )}
              {cfg.link_source === 'setting' && (
                <>
                  Using the address set below:{' '}
                  <b style={{ wordBreak: 'break-all' }}>{cfg.resolved_link}</b>
                  <Typography component="div" sx={{ fontSize: 11.5, mt: 0.4, opacity: 0.85 }}>
                    Clear the box to go back to detecting it automatically.
                  </Typography>
                </>
              )}
              {cfg.link_source === 'none' && (
                <>Could not work out the address from this connection — set it below.</>
              )}
            </Alert>
            <TextField
              fullWidth size="small" label="Override the link (optional)"
              placeholder={cfg.resolved_link || 'https://portal.yourschool.edu.gh'}
              value={cfg.link || ''}
              onChange={e => setCfg(c => ({ ...c, link: e.target.value }))}
              helperText="Leave blank to detect it automatically from wherever the app is served."
              InputProps={{
                startAdornment: <InputAdornment position="start"><LinkIcon fontSize="small" /></InputAdornment>,
              }}
            />
            <TextField
              fullWidth multiline minRows={4} size="small" label="Message"
              value={cfg.template || ''}
              onChange={e => setCfg(c => ({ ...c, template: e.target.value }))}
            />
            <Box>
              <Typography sx={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.5,
                textTransform: 'uppercase', color: 'text.secondary', mb: 0.6 }}>
                Placeholders
              </Typography>
              <Stack direction="row" spacing={0.6} flexWrap="wrap" useFlexGap>
                {['{school}', '{student}', '{index}', '{otp}', '{days}', '{link}', '{signin}', '{class}', '{guardian}']
                  .map(t => (
                    <Chip key={t} size="small" label={t} variant="outlined"
                      onClick={() => setCfg(c => ({ ...c, template: `${c.template || ''}${t}` }))}
                      sx={{ height: 22, fontSize: 11, fontFamily: 'monospace', cursor: 'pointer' }} />
                  ))}
              </Stack>
            </Box>
            <Box sx={{
              p: 1.5, borderRadius: 2, bgcolor: (t) => alpha(t.palette.info.main, 0.06),
              border: '1px solid', borderColor: (t) => alpha(t.palette.info.main, 0.25),
            }}>
              <Typography sx={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.5,
                textTransform: 'uppercase', color: 'text.secondary', mb: 0.6 }}>
                What one parent will receive
              </Typography>
              <Typography sx={{ fontSize: 13, lineHeight: 1.5 }}>{cfgPreview}</Typography>
              <Typography sx={{ fontSize: 11, color: 'text.secondary', mt: 0.8 }}>
                {cfgPreview.length} characters · {segments} SMS segment{segments === 1 ? '' : 's'} per parent
                {counts.students > 0 && ` · about ${segments * counts.students} credits for the whole school`}
              </Typography>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setCfgOpen(false)} sx={{ fontWeight: 700 }}>Cancel</Button>
          <Button variant="contained" onClick={saveCfg} disabled={cfgSaving}
            sx={{ fontWeight: 800 }}>
            {cfgSaving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Mobile money ─────────────────────────────────────────── */}
      <Dialog open={momoOpen} onClose={() => setMomoOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>Mobile money payments</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2.2} sx={{ pt: 0.5 }}>

            <FormControlLabel
              control={<Switch checked={!!momo.enabled} onChange={setM('enabled')} />}
              label={
                <Box>
                  <Typography sx={{ fontWeight: 800, fontSize: 14 }}>
                    Let parents pay from the portal
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                    Off means the portal shows balances only and tells parents to pay at
                    the office.
                  </Typography>
                </Box>
              }
            />

            {/* The sandbox takes no real money. A school that switches
                payments on without noticing which adapter is selected
                would show parents a working payment screen that settles
                nothing — worth an unmissable warning, not a footnote. */}
            {momo.enabled && momo.provider === 'mock' && (
              <Alert severity="warning">
                <b>Test mode.</b> The sandbox provider approves payments without moving any
                money. Fine for trying the screens out — switch to the bank before telling
                parents about it.
              </Alert>
            )}

            <TextField select fullWidth size="small" label="Provider"
              value={momo.provider || 'mock'} onChange={setM('provider')}
              helperText="The bank adapter reads the credentials below.">
              <MenuItem value="mock">Sandbox (test only — no real money)</MenuItem>
              <MenuItem value="bank">Bank API</MenuItem>
            </TextField>

            {momo.provider === 'bank' && (
              <>
                <Divider textAlign="left" sx={{ fontSize: 12, fontWeight: 700 }}>
                  Bank credentials
                </Divider>
                <TextField fullWidth size="small" label="API base URL"
                  value={momo.api_base || ''} onChange={setM('api_base')}
                  placeholder="https://api.yourbank.com" />
                <TextField fullWidth size="small" label="Merchant / account ID"
                  value={momo.merchant_id || ''} onChange={setM('merchant_id')} />
                {/* Write-only. The server never sends these back, so a
                    blank box means "leave the stored one alone" rather
                    than "erase it". */}
                <TextField fullWidth size="small" label="API key" type="password"
                  value={momo.api_key || ''} onChange={setM('api_key')}
                  placeholder={momo.api_key_set ? '•••••••• stored — type to replace' : ''}
                  helperText={momo.api_key_set ? 'A key is stored. Leave blank to keep it.' : ' '} />
                <TextField fullWidth size="small" label="API secret" type="password"
                  value={momo.api_secret || ''} onChange={setM('api_secret')}
                  placeholder={momo.api_secret_set ? '•••••••• stored — type to replace' : ''}
                  helperText={momo.api_secret_set ? 'A secret is stored. Leave blank to keep it.' : ' '} />
                <TextField fullWidth size="small" label="Callback secret" type="password"
                  value={momo.callback_secret || ''} onChange={setM('callback_secret')}
                  placeholder={momo.callback_secret_set ? '•••••••• stored — type to replace' : ''}
                  helperText="Used to check the bank's webhook signature." />
              </>
            )}

            <Divider textAlign="left" sx={{ fontSize: 12, fontWeight: 700 }}>
              Charges and limits
            </Divider>

            <TextField select fullWidth size="small" label="Who pays the transaction fee"
              value={momo.fee_mode || 'parent'} onChange={setM('fee_mode')}>
              <MenuItem value="parent">
                The parent — added on top of what they owe
              </MenuItem>
              <MenuItem value="school">
                The school — the parent is debited exactly what is owed
              </MenuItem>
            </TextField>

            <Grid container spacing={1.5}>
              <Grid item xs={4}>
                <TextField fullWidth size="small" label="Fee %" type="number"
                  value={momo.fee_percent ?? ''} onChange={setM('fee_percent')} />
              </Grid>
              <Grid item xs={4}>
                <TextField fullWidth size="small" label="Flat fee" type="number"
                  value={momo.fee_flat ?? ''} onChange={setM('fee_flat')}
                  InputProps={{ startAdornment:
                    <InputAdornment position="start">GHS</InputAdornment> }} />
              </Grid>
              <Grid item xs={4}>
                <TextField fullWidth size="small" label="Fee cap" type="number"
                  value={momo.fee_cap ?? ''} onChange={setM('fee_cap')}
                  helperText="0 = none"
                  InputProps={{ startAdornment:
                    <InputAdornment position="start">GHS</InputAdornment> }} />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth size="small" label="Smallest payment" type="number"
                  value={momo.min_amount ?? ''} onChange={setM('min_amount')}
                  InputProps={{ startAdornment:
                    <InputAdornment position="start">GHS</InputAdornment> }} />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth size="small" label="Largest single payment" type="number"
                  value={momo.max_amount ?? ''} onChange={setM('max_amount')}
                  InputProps={{ startAdornment:
                    <InputAdornment position="start">GHS</InputAdornment> }} />
              </Grid>
            </Grid>

            {/* Where the money lands. Said here because it is the thing
                the bursar most needs to know and least expects. */}
            <Alert severity="info" sx={{ fontSize: 13 }}>
              A successful payment does <b>not</b> post itself. It arrives in
              <b> Approve Collections</b> like any other collection, and a receipt is issued
              when someone approves it.
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setMomoOpen(false)} sx={{ fontWeight: 700 }}>Cancel</Button>
          <Button variant="contained" onClick={saveMomo} disabled={momoSaving}
            sx={{ fontWeight: 800 }}>
            {momoSaving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {filter === 'without' && counts.without > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Showing <b>{counts.without}</b> student(s) without a PIN. Click <b>Bulk</b> above to issue PINs for all of them in one pass.
        </Alert>
      )}

      <Card>
        {/* The roster is long; a header that scrolls away leaves the
            reader guessing which column a PIN is in. */}
        <TableContainer sx={{ maxHeight: 'calc(100vh - 340px)' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 700, fontSize: 11 } }}>
                <TableCell sx={{ width: 50 }}>#</TableCell>
                <TableCell>Index (username)</TableCell>
                <TableCell>Student</TableCell>
                <TableCell>Class</TableCell>
                <TableCell>Guardian</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>One-time code</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Texted</TableCell>
                <TableCell align="right" sx={{ width: 250 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && <TableRow><TableCell colSpan={10} align="center" sx={{ py: 4 }}><CircularProgress size={20} /></TableCell></TableRow>}
              {!loading && rows.length === 0 && (
                <TableRow><TableCell colSpan={10} align="center" sx={{ py: 5, color: 'text.secondary' }}>
                  <NoPinIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                  <Typography sx={{ fontWeight: 700, fontSize: 14 }}>
                    No learners match these filters
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    This list covers learners currently on the roll — completed
                    learners are not shown.
                  </Typography>
                </TableCell></TableRow>
              )}
              {!loading && rows.map((r, i) => {
                const hasPin = !!r.pin;
                const isBlocked = r.pin_state === 'blocked';
                const busy = busyInd === r.ind;
                const initials = String(r.name || '?')
                  .trim().split(/\s+/).slice(0, 2).map(w => w[0] || '').join('').toUpperCase() || '?';
                return (
                  <TableRow key={r.ind || i} hover sx={{
                    '& td': { fontSize: 12.5, py: 0.4 },
                    bgcolor: busy ? (t) => alpha(t.palette.primary.main, 0.05) : undefined,
                  }}>
                    <TableCell sx={{ color: 'text.secondary' }}>{i + 1}</TableCell>
                    <TableCell sx={{ fontWeight: 800, fontFamily: 'monospace' }}>{r.ind}</TableCell>
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Box sx={{
                          width: 26, height: 26, borderRadius: '50%', flexShrink: 0,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: 10, fontWeight: 800,
                          bgcolor: (t) => alpha(t.palette.primary.main, 0.12),
                          color: 'primary.main',
                        }}>
                          {initials}
                        </Box>
                        <Typography sx={{ fontWeight: 600, fontSize: 12.5 }}>{r.name}</Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      {r.cla
                        ? <Chip size="small" variant="outlined" label={r.cla}
                            sx={{ height: 20, fontWeight: 700, fontSize: 11 }} />
                        : <em style={{ color: '#999' }}>unassigned</em>}
                    </TableCell>
                    <TableCell>{r.guardian || <em style={{ color: '#999' }}>not on file</em>}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace' }}>{r.phone || <em style={{ color: '#999' }}>—</em>}</TableCell>
                    <TableCell>
                      {hasPin
                        ? <Chip size="small" label={r.pin}
                            color={isBlocked ? 'default' : 'primary'}
                            sx={{ fontFamily: 'monospace', fontWeight: 800, letterSpacing: 1.2, height: 22 }} />
                        : <em style={{ color: '#999' }}>—</em>}
                    </TableCell>
                    <TableCell>
                      {(() => {
                        const st = STATE_CHIP[r.cred_state] || STATE_CHIP.none;
                        return (
                          <Tooltip title={
                            r.cred_state === 'used'
                              ? `Parent set their own passcode ${fmtWhen(r.password_set_at)} — the school cannot see it`
                              : r.cred_state === 'active'
                                ? `Code valid until ${String(r.otp_expires_at || '').slice(0, 10) || 'further notice'}`
                                : r.cred_state === 'expired'
                                  ? 'This code has lapsed — reissue before sending'
                                  : r.cred_state === 'blocked'
                                    ? 'Sign-in refused until unblocked'
                                    : 'No code has been issued yet'
                          }>
                            <Chip size="small" label={st.label} color={st.color}
                              sx={{ height: 20, fontWeight: 700 }} />
                          </Tooltip>
                        );
                      })()}
                      {r.locked && (
                        <Tooltip title="Temporarily locked after too many failed attempts">
                          <Chip size="small" label="locked" color="error" variant="outlined"
                            sx={{ height: 18, fontSize: 10, fontWeight: 700, ml: 0.5 }} />
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell>
                      {r.last_sms_at ? (
                        <Tooltip title={`Last texted to ${r.last_sms_to || 'guardian'} · ${r.sms_count} time(s)`}>
                          <Typography sx={{ fontSize: 11.5, color: 'text.secondary' }}>
                            {fmtWhen(r.last_sms_at)}
                          </Typography>
                        </Tooltip>
                      ) : (
                        <Typography sx={{ fontSize: 11.5, color: 'text.disabled' }}>never</Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {busy ? <CircularProgress size={16} /> : (
                        <Stack direction="row" spacing={0.3} justifyContent="flex-end">
                          {r.cred_state === 'none' && (
                            <Tooltip title="Issue a one-time code"><IconButton size="small" color="primary"
                              onClick={() => generate(r.ind)}><GenerateIcon fontSize="small" /></IconButton></Tooltip>
                          )}
                          {r.cred_state !== 'none' && (
                            <>
                              {/* Texting is only offered where it would work.
                                  A disabled button with a reason beats one
                                  that fires and reports a skip. */}
                              <Tooltip title={
                                r.cred_state !== 'active' ? 'Only a live code can be texted — reissue first'
                                  : !r.phone ? 'No phone number on file for this guardian'
                                  : `Text the code to ${r.phone}`
                              }>
                                <span>
                                  <IconButton size="small" color="success"
                                    disabled={r.cred_state !== 'active' || !r.phone}
                                    onClick={() => sendOne(r)}><SendIcon fontSize="small" /></IconButton>
                                </span>
                              </Tooltip>
                              {r.cred_state === 'active' && (
                                <Tooltip title="Copy the sign-in details"><IconButton size="small"
                                  onClick={() => copyCreds(r)}><CopyIcon fontSize="small" /></IconButton></Tooltip>
                              )}
                              <Tooltip title="Issue a fresh code (clears any passcode the parent set)">
                                <IconButton size="small" color="warning"
                                  onClick={() => reset(r.ind)}><ResetIcon fontSize="small" /></IconButton></Tooltip>
                              {!isBlocked
                                ? <Tooltip title="Block this login"><IconButton size="small" color="error"
                                    onClick={() => block(r.ind)}><BlockIcon fontSize="small" /></IconButton></Tooltip>
                                : <Tooltip title="Unblock"><IconButton size="small" color="success"
                                    onClick={() => unblock(r.ind)}><UnblockIcon fontSize="small" /></IconButton></Tooltip>
                              }
                              <Tooltip title="Remove portal access entirely"><IconButton size="small" color="error"
                                onClick={() => remove(r.ind)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                            </>
                          )}
                        </Stack>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      <Box sx={{ mt: 1.5 }}>
        <Typography variant="caption" color="text.secondary">
          The parent receives their child's <b>index number</b> and a <b>6-digit one-time code</b>
          {' '}by SMS, valid for {cfg.otp_ttl_days ?? 7} days. They open the link, sign in with
          those two, and are then made to choose their own 6-digit passcode — which nobody at
          the school can see. If they forget it, issue a fresh code and text it again.
        </Typography>
      </Box>
    </Box>
  );
}
